"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Instruction = void 0;
const util_1 = require("@polkadot/util");
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const bluebird_1 = __importDefault(require("bluebird"));
const executeManualInstruction_1 = require("../../procedures/executeManualInstruction");
const internal_1 = require("../../../internal");
const settlements_1 = require("../../../middleware/queries/settlements");
const types_1 = require("../../../middleware/types");
const types_2 = require("../../../types");
const internal_2 = require("../../../types/internal");
const utils_1 = require("../../../utils");
const conversion_1 = require("../../../utils/conversion");
const internal_3 = require("../../../utils/internal");
const types_3 = require("./types");
const executedMessage = 'Instruction has already been executed/rejected and it was purged from chain';
/**
 * Represents a settlement Instruction to be executed on a certain Venue
 */
class Instruction extends internal_1.Entity {
    /**
     * @hidden
     */
    constructor(identifiers, context) {
        super(identifiers, context);
        const { id } = identifiers;
        this.id = id;
        this.reject = (0, internal_3.createProcedureMethod)({
            getProcedureAndArgs: args => [
                internal_1.modifyInstructionAffirmation,
                Object.assign({ id, operation: types_2.InstructionAffirmationOperation.Reject }, args),
            ],
            optionalArgs: true,
        }, context);
        this.affirm = (0, internal_3.createProcedureMethod)({
            getProcedureAndArgs: args => [
                internal_1.modifyInstructionAffirmation,
                Object.assign({ id, operation: types_2.InstructionAffirmationOperation.Affirm }, args),
            ],
            optionalArgs: true,
        }, context);
        this.withdraw = (0, internal_3.createProcedureMethod)({
            getProcedureAndArgs: args => [
                internal_1.modifyInstructionAffirmation,
                Object.assign({ id, operation: types_2.InstructionAffirmationOperation.Withdraw }, args),
            ],
            optionalArgs: true,
        }, context);
        this.rejectAsMediator = (0, internal_3.createProcedureMethod)({
            getProcedureAndArgs: () => [
                internal_1.modifyInstructionAffirmation,
                { id, operation: types_2.InstructionAffirmationOperation.RejectAsMediator },
            ],
            voidArgs: true,
        }, context);
        this.affirmAsMediator = (0, internal_3.createProcedureMethod)({
            getProcedureAndArgs: args => [
                internal_1.modifyInstructionAffirmation,
                Object.assign({ id, operation: types_2.InstructionAffirmationOperation.AffirmAsMediator }, args),
            ],
            optionalArgs: true,
        }, context);
        this.withdrawAsMediator = (0, internal_3.createProcedureMethod)({
            getProcedureAndArgs: () => [
                internal_1.modifyInstructionAffirmation,
                { id, operation: types_2.InstructionAffirmationOperation.WithdrawAsMediator },
            ],
            voidArgs: true,
        }, context);
        this.executeManually = (0, internal_3.createProcedureMethod)({
            getProcedureAndArgs: args => [executeManualInstruction_1.executeManualInstruction, Object.assign({ id }, args)],
            optionalArgs: true,
        }, context);
    }
    /**
     * @hidden
     * Check if a value is of type {@link UniqueIdentifiers}
     */
    static isUniqueIdentifiers(identifier) {
        const { id } = identifier;
        return id instanceof bignumber_js_1.default;
    }
    /**
     * Retrieve whether the Instruction has already been executed and pruned from
     *   the chain.
     */
    isExecuted() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { settlement }, }, }, id, context, } = this;
            const [status, exists] = yield Promise.all([
                settlement.instructionStatuses((0, conversion_1.bigNumberToU64)(id, context)),
                this.exists(),
            ]);
            const statusResult = (0, conversion_1.meshInstructionStatusToInstructionStatus)(status);
            return ((statusResult === internal_2.InstructionStatus.Unknown ||
                statusResult === internal_2.InstructionStatus.Success ||
                statusResult === internal_2.InstructionStatus.Rejected) &&
                exists);
        });
    }
    /**
     * Retrieve whether the Instruction is still pending on chain
     */
    isPending() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { settlement }, }, }, id, context, } = this;
            const status = yield settlement.instructionStatuses((0, conversion_1.bigNumberToU64)(id, context));
            const statusResult = (0, conversion_1.meshInstructionStatusToInstructionStatus)(status);
            return statusResult === internal_2.InstructionStatus.Pending;
        });
    }
    /**
     * Retrieve current status of the Instruction. This can be subscribed to know if instruction fails
     *
     * @note can be subscribed to, if connected to node using a web socket
     * @note current status as `Executed` means that the Instruction has been executed/rejected and pruned from
     *   the chain.
     */
    onStatusChange(callback) {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { settlement }, }, }, id, context, } = this;
            context.assertSupportsSubscription();
            const assembleResult = (rawStatus) => {
                const internalStatus = (0, conversion_1.meshInstructionStatusToInstructionStatus)(rawStatus);
                const status = this.internalToExternalStatus(internalStatus);
                if (!status) {
                    throw new Error('Unknown instruction status');
                }
                return status;
            };
            return settlement.instructionStatuses((0, conversion_1.bigNumberToU64)(id, context), status => {
                return callback(assembleResult(status));
            });
        });
    }
    /**
     * Determine whether this Instruction exists on chain (or existed and was pruned)
     */
    exists() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { settlement }, }, }, id, } = this;
            const instructionCounter = yield settlement.instructionCounter();
            return id.gt(new bignumber_js_1.default(0)) && id.lt((0, conversion_1.u64ToBigNumber)(instructionCounter));
        });
    }
    /**
     * This method verifies whether the instruction exists and returns if middleware is available.
     *
     * @throws if instruction does not exists
     */
    getMiddlewareInfoAndCheckIfInstructionExists() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context } = this;
            const [isMiddlewareAvailable, exists] = yield Promise.all([
                context.isMiddlewareAvailable(),
                this.exists(),
            ]);
            if (!exists) {
                throw new internal_1.PolymeshError({
                    code: types_2.ErrorCode.DataUnavailable,
                    message: 'Instruction does not exists',
                    data: {
                        instructionId: this.id,
                    },
                });
            }
            return isMiddlewareAvailable;
        });
    }
    /**
     * Fetches the instruction details from middleware
     *
     * @throws if instruction data is not yet processed by the middleware
     */
    getInstructionFromMiddleware() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context, id } = this;
            const { data: { instructions: { nodes: [instruction], }, }, } = yield context.queryMiddleware((0, settlements_1.instructionsQuery)(context.isSqIdPadded, {
                id: id.toString(),
            }));
            if (!instruction) {
                throw new internal_1.PolymeshError({
                    code: types_2.ErrorCode.DataUnavailable,
                    message: 'Instruction is not yet processed by the middleware',
                });
            }
            return instruction;
        });
    }
    /**
     * Asserts that start value of pagination options based on middleware availability
     */
    assertPaginationStart(isMiddlewareAvailable, paginationOpts) {
        if (!paginationOpts) {
            return;
        }
        if (isMiddlewareAvailable) {
            if (typeof paginationOpts.start === 'string') {
                throw new internal_1.PolymeshError({
                    code: types_2.ErrorCode.ValidationError,
                    message: '`start` should be of type BigNumber to query the data from middleware',
                });
            }
        }
        else if (typeof paginationOpts.start !== 'string') {
            throw new internal_1.PolymeshError({
                code: types_2.ErrorCode.ValidationError,
                message: '`start` should be of type string to query the data from chain',
            });
        }
    }
    /**
     * @hidden
     *
     * Retrieve information specific to this Instruction from chain
     *
     * @throws if instruction is executed/rejected and was pruned from chain
     */
    detailsFromChain() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { settlement }, }, }, id, context, } = this;
            const rawId = (0, conversion_1.bigNumberToU64)(id, context);
            const [{ createdAt, tradeDate, valueDate, settlementType: type, venueId }, rawStatus, memo] = yield (0, internal_3.requestMulti)(context, [
                [settlement.instructionDetails, rawId],
                [settlement.instructionStatuses, rawId],
                [settlement.instructionMemos, rawId],
            ]);
            const internalStatus = (0, conversion_1.meshInstructionStatusToInstructionStatus)(rawStatus);
            const status = this.internalToExternalStatus(internalStatus);
            if (!status) {
                throw new internal_1.PolymeshError({
                    code: types_2.ErrorCode.DataUnavailable,
                    message: executedMessage,
                });
            }
            return Object.assign({ status, createdAt: createdAt.isSome ? (0, conversion_1.momentToDate)(createdAt.unwrap()) : null, tradeDate: tradeDate.isSome ? (0, conversion_1.momentToDate)(tradeDate.unwrap()) : null, valueDate: valueDate.isSome ? (0, conversion_1.momentToDate)(valueDate.unwrap()) : null, venue: venueId.isSome ? new internal_1.Venue({ id: (0, conversion_1.u64ToBigNumber)(venueId.unwrap()) }, context) : null, memo: memo.isSome ? (0, conversion_1.instructionMemoToString)(memo.unwrap()) : null }, (0, conversion_1.meshSettlementTypeToEndCondition)(type));
        });
    }
    /**
     * Retrieve information specific to this Instruction
     *
     * @note uses middleware (if available) to retrieve information, otherwise directly queries from the chain
     *
     * @throws if
     *  - instruction does not exists
     *  - instruction is not yet processed by the middleware (when querying from middleware)
     *  - instruction is executed/rejected and was pruned from chain (when querying from chain)
     */
    details() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context } = this;
            const isMiddlewareAvailable = yield this.getMiddlewareInfoAndCheckIfInstructionExists();
            if (isMiddlewareAvailable) {
                const instruction = yield this.getInstructionFromMiddleware();
                const endCondition = (0, conversion_1.middlewareInstructionToInstructionEndCondition)(instruction);
                const { status, tradeDate, valueDate, venueId, memo, createdBlock } = instruction;
                return Object.assign({ status: (0, conversion_1.middlewareInstructionStatusToInstructionStatus)(status), createdAt: new Date(createdBlock.datetime), tradeDate: tradeDate ? new Date(tradeDate) : null, valueDate: valueDate ? new Date(valueDate) : null, venue: venueId ? new internal_1.Venue({ id: new bignumber_js_1.default(venueId) }, context) : null, memo: memo !== null && memo !== void 0 ? memo : null }, endCondition);
            }
            return this.detailsFromChain();
        });
    }
    /**
     * Retrieve every authorization generated by this Instruction (status and authorizing Identity)
     *
     * @note supports pagination.
     * @note uses middleware (if available) to retrieve information, otherwise directly queries from the chain
     *
     * @throws if
     *  - instruction does not exists
     *  - instruction is executed/rejected and was pruned from chain (when querying from chain)
     */
    getAffirmations(paginationOpts) {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { settlement }, }, }, id, context, } = this;
            const isMiddlewareAvailable = yield this.getMiddlewareInfoAndCheckIfInstructionExists();
            this.assertPaginationStart(isMiddlewareAvailable, paginationOpts);
            if (isMiddlewareAvailable) {
                const start = paginationOpts === null || paginationOpts === void 0 ? void 0 : paginationOpts.start;
                const { data: { instructionAffirmations: { nodes: affirmations, totalCount }, }, } = yield context.queryMiddleware((0, settlements_1.instructionAffirmationsQuery)(context.isSqIdPadded, {
                    instructionId: id.toString(),
                }, paginationOpts === null || paginationOpts === void 0 ? void 0 : paginationOpts.size, start));
                const count = new bignumber_js_1.default(totalCount);
                const data = affirmations.map(affirmation => {
                    const { identity, status } = affirmation;
                    return {
                        identity: new internal_1.Identity({ did: identity }, context),
                        status: (0, conversion_1.middlewareAffirmStatusToAffirmationStatus)(status),
                    };
                });
                const next = (0, internal_3.calculateNextKey)(count, data.length, start);
                return {
                    data,
                    count,
                    next,
                };
            }
            const isExecuted = yield this.isExecuted();
            if (isExecuted) {
                throw new internal_1.PolymeshError({
                    code: types_2.ErrorCode.DataUnavailable,
                    message: executedMessage,
                });
            }
            const { entries, lastKey: next } = yield (0, internal_3.requestPaginated)(settlement.affirmsReceived, {
                arg: (0, conversion_1.bigNumberToU64)(id, context),
                paginationOpts: paginationOpts,
            });
            const data = entries.map(([{ args }, meshAffirmationStatus]) => {
                const [, { did }] = args;
                return {
                    identity: new internal_1.Identity({ did: (0, conversion_1.identityIdToString)(did) }, context),
                    status: (0, conversion_1.meshAffirmationStatusToAffirmationStatus)(meshAffirmationStatus),
                };
            });
            return {
                data,
                next,
            };
        });
    }
    /**
     * @hidden
     *
     * Retrieve all legs of this Instruction from chain
     *
     * @note supports pagination
     *
     * @throws if the instruction is executed/rejected and was pruned from chain
     */
    getLegsFromChain(paginationOpts) {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { settlement }, }, }, context, id, } = this;
            const instruction = new Instruction({ id }, context);
            const isExecuted = yield instruction.isExecuted();
            if (isExecuted) {
                throw new internal_1.PolymeshError({
                    code: types_2.ErrorCode.DataUnavailable,
                    message: 'Instruction has already been executed/rejected and it was purged from chain',
                });
            }
            const { entries: legs, lastKey: next } = yield (0, internal_3.requestPaginated)(settlement.instructionLegs, {
                arg: (0, conversion_1.bigNumberToU64)(id, context),
                paginationOpts: paginationOpts,
            });
            const data = [...legs]
                .sort((a, b) => (0, conversion_1.u64ToBigNumber)(a[0].args[1]).minus((0, conversion_1.u64ToBigNumber)(b[0].args[1])).toNumber())
                .map(([, leg]) => {
                if (leg.isSome) {
                    const legValue = leg.unwrap();
                    if (legValue.isFungible) {
                        const { sender, receiver, amount, assetId: rawAssetId } = legValue.asFungible;
                        const assetId = (0, conversion_1.assetIdToString)(rawAssetId);
                        const fromPortfolio = (0, conversion_1.meshPortfolioIdToPortfolio)(sender, context);
                        const toPortfolio = (0, conversion_1.meshPortfolioIdToPortfolio)(receiver, context);
                        return {
                            from: fromPortfolio,
                            to: toPortfolio,
                            amount: (0, conversion_1.balanceToBigNumber)(amount),
                            asset: new internal_1.FungibleAsset({ assetId }, context),
                        };
                    }
                    else if (legValue.isNonFungible) {
                        const { sender, receiver, nfts } = legValue.asNonFungible;
                        const from = (0, conversion_1.meshPortfolioIdToPortfolio)(sender, context);
                        const to = (0, conversion_1.meshPortfolioIdToPortfolio)(receiver, context);
                        const { assetId, ids } = (0, conversion_1.meshNftToNftId)(nfts);
                        return {
                            from,
                            to,
                            nfts: ids.map(nftId => new internal_1.Nft({ assetId, id: nftId }, context)),
                            asset: new internal_1.NftCollection({ assetId }, context),
                        };
                    }
                    else {
                        const { senderIdentity, receiverIdentity, amount, ticker: rawTicker, } = legValue.asOffChain;
                        const ticker = (0, conversion_1.tickerToString)(rawTicker);
                        const from = (0, conversion_1.identityIdToString)(senderIdentity);
                        const to = (0, conversion_1.identityIdToString)(receiverIdentity);
                        return {
                            from: new internal_1.Identity({ did: from }, context),
                            to: new internal_1.Identity({ did: to }, context),
                            offChainAmount: (0, conversion_1.balanceToBigNumber)(amount),
                            asset: ticker,
                        };
                    }
                }
                else {
                    throw new Error('Instruction has already been executed/rejected and it was purged from chain');
                }
            });
            return {
                data,
                next,
            };
        });
    }
    /**
     * Retrieve all legs of this Instruction
     *
     * @note supports pagination
     * @note uses middleware (if available) to retrieve information, otherwise directly queries from the chain
     *
     * @throws if
     *  - instruction does not exists
     *  - instruction is not yet processed by the middleware (when querying from middleware)
     *  - instruction is executed/rejected and was pruned from chain (when querying from chain)
     */
    getLegs(paginationOpts) {
        return __awaiter(this, void 0, void 0, function* () {
            const { id, context } = this;
            const isMiddlewareAvailable = yield this.getMiddlewareInfoAndCheckIfInstructionExists();
            this.assertPaginationStart(isMiddlewareAvailable, paginationOpts);
            if (isMiddlewareAvailable) {
                const start = paginationOpts === null || paginationOpts === void 0 ? void 0 : paginationOpts.start;
                const { data: { legs: { nodes, totalCount }, }, } = yield context.queryMiddleware((0, settlements_1.legsQuery)({
                    instructionId: id.toString(),
                }, paginationOpts === null || paginationOpts === void 0 ? void 0 : paginationOpts.size, start));
                const data = nodes.map(leg => (0, conversion_1.middlewareLegToLeg)(leg, context));
                const count = new bignumber_js_1.default(totalCount);
                const next = (0, internal_3.calculateNextKey)(count, data.length, start);
                return {
                    data,
                    count,
                    next,
                };
            }
            return this.getLegsFromChain(paginationOpts);
        });
    }
    /**
     * Retrieve current status of this Instruction
     *
     * @note uses the middlewareV2
     */
    getStatus() {
        return __awaiter(this, void 0, void 0, function* () {
            const isPending = yield this.isPending();
            if (isPending) {
                return {
                    status: types_3.InstructionStatus.Pending,
                };
            }
            const [executedEventIdentifier, failedEventIdentifier, failedToExecuteIdentifier, rejectedEventIdentifier,] = yield Promise.all([
                this.getInstructionEventFromMiddleware(types_1.InstructionEventEnum.InstructionExecuted),
                this.getInstructionEventFromMiddleware(types_1.InstructionEventEnum.InstructionFailed),
                this.getInstructionEventFromMiddleware(types_1.InstructionEventEnum.FailedToExecuteInstruction),
                this.getInstructionEventFromMiddleware(types_1.InstructionEventEnum.InstructionRejected),
            ]);
            if (executedEventIdentifier) {
                return {
                    status: types_3.InstructionStatus.Success,
                    eventIdentifier: executedEventIdentifier,
                };
            }
            if (failedEventIdentifier) {
                return {
                    status: types_3.InstructionStatus.Failed,
                    eventIdentifier: failedEventIdentifier,
                };
            }
            if (failedToExecuteIdentifier) {
                return {
                    status: types_3.InstructionStatus.Failed,
                    eventIdentifier: failedToExecuteIdentifier,
                };
            }
            if (rejectedEventIdentifier) {
                return {
                    status: types_3.InstructionStatus.Rejected,
                    eventIdentifier: rejectedEventIdentifier,
                };
            }
            throw new internal_1.PolymeshError({
                code: types_2.ErrorCode.DataUnavailable,
                message: "It isn't possible to determine the current status of this Instruction",
            });
        });
    }
    /**
     * @hidden
     * Retrieve Instruction status event from middleware V2
     */
    getInstructionEventFromMiddleware(event) {
        return __awaiter(this, void 0, void 0, function* () {
            const { id, context } = this;
            const { data: { instructionEvents: { nodes: [details], }, }, } = yield context.queryMiddleware((0, settlements_1.instructionEventsQuery)(context.isSqIdPadded, {
                event,
                instructionId: id.toString(),
            }, new bignumber_js_1.default(1), new bignumber_js_1.default(0)));
            return (0, internal_3.optionize)(conversion_1.middlewareEventDetailsToEventIdentifier)(details === null || details === void 0 ? void 0 : details.updatedBlock, details === null || details === void 0 ? void 0 : details.eventIdx);
        });
    }
    /**
     * Return the Instruction's ID
     */
    toHuman() {
        return this.id.toString();
    }
    /**
     * Retrieve all the involved portfolios in this Instruction where the given identity is a custodian of
     */
    getInvolvedPortfolios(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const { did } = args;
            const { data: legs } = yield this.getLegs();
            const assemblePortfolios = (involvedPortfolios, leg) => __awaiter(this, void 0, void 0, function* () {
                if (!(0, utils_1.isOffChainLeg)(leg)) {
                    const { from, to } = leg;
                    const [fromExists, toExists] = yield Promise.all([from.exists(), to.exists()]);
                    const checkCustody = (legPortfolio, exists) => __awaiter(this, void 0, void 0, function* () {
                        if (exists) {
                            const isCustodied = yield legPortfolio.isCustodiedBy({ identity: did });
                            if (isCustodied) {
                                involvedPortfolios.push(legPortfolio);
                            }
                        }
                        else if (legPortfolio.owner.did === did) {
                            involvedPortfolios.push(legPortfolio);
                        }
                    });
                    yield Promise.all([checkCustody(from, fromExists), checkCustody(to, toExists)]);
                }
                return involvedPortfolios;
            });
            const portfolios = yield bluebird_1.default.reduce(legs, (result, leg) => __awaiter(this, void 0, void 0, function* () { return assemblePortfolios(result, leg); }), []);
            return portfolios;
        });
    }
    /**
     * Returns the mediators for the Instruction, along with their affirmation status
     *
     * @note uses middleware (if available) to retrieve information, otherwise directly queries from the chain
     *
     * @throws if
     *  - instruction does not exists
     *  - instruction is not yet processed by the middleware (when querying from middleware)
     *  - instruction is executed/rejected and was pruned from chain (when querying from chain)
     */
    getMediators() {
        return __awaiter(this, void 0, void 0, function* () {
            const { id, context, context: { polymeshApi: { query: { settlement }, }, }, } = this;
            const isMiddlewareAvailable = yield this.getMiddlewareInfoAndCheckIfInstructionExists();
            if (isMiddlewareAvailable) {
                const [{ mediators }, { data: { instructionAffirmations: { nodes: mediatorAffirmations }, }, },] = yield Promise.all([
                    this.getInstructionFromMiddleware(),
                    context.queryMiddleware((0, settlements_1.instructionAffirmationsQuery)(context.isSqIdPadded, {
                        instructionId: id.toString(),
                        isMediator: true,
                    })),
                ]);
                const getMediatorAffirmationStatus = (mediatorAffirmation) => {
                    if (mediatorAffirmation) {
                        const status = (0, conversion_1.middlewareAffirmStatusToAffirmationStatus)(mediatorAffirmation.status);
                        return {
                            status,
                            expiry: mediatorAffirmation.expiry ? new Date(mediatorAffirmation.expiry) : undefined,
                        };
                    }
                    return { status: types_3.AffirmationStatus.Pending };
                };
                return mediators.map(mediator => {
                    const affirmation = mediatorAffirmations.find(mediatorAffirmation => mediatorAffirmation.identity === mediator);
                    return Object.assign({ identity: new internal_1.Identity({ did: mediator }, context) }, getMediatorAffirmationStatus(affirmation));
                });
            }
            const rawId = (0, conversion_1.bigNumberToU64)(id, context);
            const rawAffirms = yield settlement.instructionMediatorsAffirmations.entries(rawId);
            return rawAffirms.map(([key, affirmStatus]) => {
                const rawDid = key.args[1];
                const did = (0, conversion_1.identityIdToString)(rawDid);
                const identity = new internal_1.Identity({ did }, context);
                const { status, expiry } = (0, conversion_1.mediatorAffirmationStatusToStatus)(affirmStatus);
                return { identity, status, expiry };
            });
        });
    }
    /**
     * Returns affirmation statuses for offchain legs in this Instruction
     *
     * @note uses middleware (if available) to retrieve information, otherwise directly queries from the chain
     *
     * @throws if
     *  - instruction does not exists
     *  - instruction is not yet processed by the middleware (when querying from middleware)
     *  - instruction is executed/rejected and was pruned from chain (when querying from chain)
     */
    getOffChainAffirmations() {
        return __awaiter(this, void 0, void 0, function* () {
            const { id, context, context: { polymeshApi: { query: { settlement }, }, }, } = this;
            const isMiddlewareAvailable = yield this.getMiddlewareInfoAndCheckIfInstructionExists();
            if (isMiddlewareAvailable) {
                const { data: { instructionAffirmations: { nodes: offChainAffirmations }, }, } = yield context.queryMiddleware((0, settlements_1.offChainAffirmationsQuery)(context.isSqIdPadded, {
                    instructionId: id.toString(),
                }));
                return offChainAffirmations.map(offChainAffirmation => {
                    const { status, offChainReceipt } = offChainAffirmation;
                    return {
                        status: (0, conversion_1.middlewareAffirmStatusToAffirmationStatus)(status),
                        legId: new bignumber_js_1.default(offChainReceipt.leg.legIndex),
                    };
                });
            }
            const rawId = (0, conversion_1.bigNumberToU64)(id, context);
            const rawAffirms = yield settlement.offChainAffirmations.entries(rawId);
            return rawAffirms.map(([{ args: [, rawLegId], }, affirmStatus,]) => {
                const legId = (0, conversion_1.u64ToBigNumber)(rawLegId);
                const status = (0, conversion_1.meshAffirmationStatusToAffirmationStatus)(affirmStatus);
                return { legId, status };
            });
        });
    }
    /**
     * Returns affirmation status for a specific offchain leg in this Instruction
     *
     * @param args.legId index of the leg whose affirmation status is to be fetched
     *
     * @note uses middleware (if available) to retrieve information, otherwise directly queries from the chain
     *
     * @throws if
     *  - instruction does not exists
     *  - legId provided is not an off-chain leg
     *  - instruction is not yet processed by the middleware (when querying from middleware)
     *  - instruction is executed/rejected and was pruned from chain (when querying from chain)
     */
    getOffChainAffirmationForLeg(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const { id, context, context: { polymeshApi: { query: { settlement }, }, }, } = this;
            const { legId } = args;
            const isMiddlewareAvailable = yield this.getMiddlewareInfoAndCheckIfInstructionExists();
            if (isMiddlewareAvailable) {
                const { data: { legs: { nodes: [leg], }, }, } = yield context.queryMiddleware((0, settlements_1.legsQuery)({
                    instructionId: id.toString(),
                    legIndex: legId.toNumber(),
                    legType: types_1.LegTypeEnum.OffChain,
                }));
                if (!leg) {
                    throw new internal_1.PolymeshError({
                        code: types_2.ErrorCode.DataUnavailable,
                        message: 'The given leg ID is not an off-chain leg',
                    });
                }
                return leg.offChainReceipts.nodes.length
                    ? types_3.AffirmationStatus.Affirmed
                    : types_3.AffirmationStatus.Pending;
            }
            const rawId = (0, conversion_1.bigNumberToU64)(id, context);
            const rawLegId = (0, conversion_1.bigNumberToU64)(legId, context);
            const rawAffirmStatus = yield settlement.offChainAffirmations(rawId, rawLegId);
            return (0, conversion_1.meshAffirmationStatusToAffirmationStatus)(rawAffirmStatus);
        });
    }
    /**
     * @hidden
     */
    internalToExternalStatus(status) {
        switch (status) {
            case internal_2.InstructionStatus.Pending:
                return types_3.InstructionStatus.Pending;
            case internal_2.InstructionStatus.Failed:
                return types_3.InstructionStatus.Failed;
            case internal_2.InstructionStatus.Success:
                return types_3.InstructionStatus.Success;
            case internal_2.InstructionStatus.Rejected:
                return types_3.InstructionStatus.Rejected;
        }
        return null;
    }
    /**
     * Generate an offchain affirmation receipt for a specific leg and UID
     *
     * @param args.legId index of the offchain leg in this instruction
     * @param args.uid UID of the receipt
     * @param args.metadata (optional) metadata to be associated with the receipt
     * @param args.signer (optional) Signer to be used to generate receipt signature. Defaults to signing Account associated with the SDK
     * @param args.signerKeyRingType (optional) keyring type of the signer. Defaults to 'Sr25519'
     */
    generateOffChainAffirmationReceipt(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const { id, context, context: { polymeshApi: { query: { settlement }, }, }, } = this;
            const { legId, uid, metadata, signer, signerKeyRingType = types_2.SignerKeyRingType.Sr25519 } = args;
            const rawId = (0, conversion_1.bigNumberToU64)(id, context);
            const rawLegId = (0, conversion_1.bigNumberToU64)(legId, context);
            const rawOptionalLeg = yield settlement.instructionLegs(rawId, rawLegId);
            if (rawOptionalLeg.isNone) {
                throw new internal_1.PolymeshError({
                    code: types_2.ErrorCode.DataUnavailable,
                    message: 'Leg does not exist',
                });
            }
            const rawLeg = rawOptionalLeg.unwrap();
            if (!rawLeg.isOffChain) {
                throw new internal_1.PolymeshError({
                    code: types_2.ErrorCode.UnmetPrerequisite,
                    message: 'Receipt payload can only be generated for offchain legs',
                });
            }
            const { senderIdentity, receiverIdentity, ticker, amount } = rawLeg.asOffChain;
            const rawUid = (0, conversion_1.bigNumberToU64)(uid, context);
            const payloadStrings = [
                rawUid.toHex(true),
                rawId.toHex(true),
                rawLegId.toHex(true),
                senderIdentity.toHex(),
                receiverIdentity.toHex(),
                ticker.toHex(),
                amount.toHex(true),
            ];
            const rawPayload = (0, util_1.hexAddPrefix)(payloadStrings.map(e => (0, util_1.hexStripPrefix)(e)).join(''));
            const signatureValue = yield context.getSignature({ rawPayload, signer });
            return {
                uid,
                legId,
                signer: signer || context.getSigningAccount(),
                signature: {
                    type: signerKeyRingType,
                    value: signatureValue,
                },
                metadata,
            };
        });
    }
}
exports.Instruction = Instruction;
//# sourceMappingURL=index.js.map